import { getBrowserNotificationIconUrl, showInPageToastWhenTabVisible } from './browserNotifications';

export function supportsServiceWorkerNotifications() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator;
}

/** Detecta el navegador para usar la estrategia más compatible */
function detectBrowser() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('opera') || ua.includes('opr/')) return 'opera';
  
  return 'unknown';
}

/** Ruta del SW respetando `homepage` / PUBLIC_URL (CRA) para que no falle en despliegue con subruta. */
export function getServiceWorkerPushUrl() {
  const prefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${prefix}/sw-push.js`;
}

/** Registra sw-push.js y espera a que esté activo y controlando la página. */
export async function registerServiceWorkerForNotifications() {
  if (!supportsServiceWorkerNotifications()) return null;
  try {
    await navigator.serviceWorker.register(getServiceWorkerPushUrl());
    const reg = await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.warn('[webPush] registerServiceWorkerForNotifications error:', err);
    return null;
  }
}

function buildShowOptions(body, { tag, url, requireInteraction, renotify, silent, icon, badge }) {
  const bodyStr = body == null ? '' : String(body);
  const defaultIcon = icon ?? getBrowserNotificationIconUrl();
  return {
    body: bodyStr,
    icon: defaultIcon,
    badge: badge ?? defaultIcon,
    tag: tag || 'hs-app',
    requireInteraction: !!requireInteraction,
    renotify: !!renotify,
    silent: !!silent,
    data: { url },
  };
}

/**
 * Obtiene (o registra) el SW y devuelve la registration con worker activo.
 * Solo en navegadores que soportan SW correctamente (Chrome, Firefox moderno, Edge).
 */
async function ensurePushRegistration() {
  const browser = detectBrowser();
  
  // Safari tiene soporte limitado de SW; mejor usar new Notification() directamente
  if (browser === 'safari') return null;
  
  if (!supportsServiceWorkerNotifications()) return null;
  try {
    await navigator.serviceWorker.register(getServiceWorkerPushUrl());
    const reg = await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.warn('[webPush] ensurePushRegistration error:', err);
    return null;
  }
}

/**
 * Notificación del sistema vía Service Worker (`registration.showNotification`).
 * Funciona bien en Chrome, Edge, Firefox moderno. No usar en Safari.
 */
async function trySwShowNotification(titleStr, notifOpts) {
  const reg = await ensurePushRegistration();
  if (!reg) {
    return false;
  }
  try {
    await reg.showNotification(titleStr, notifOpts);
    return true;
  } catch (err) {
    console.warn('[webPush] reg.showNotification error:', err);
    return false;
  }
}

/**
 * Notificación del sistema vía `new Notification()`.
 * Funciona en todos los navegadores que soportan la API Notification.
 * Incluye workarounds específicos para Firefox.
 */
function tryDirectNotification(titleStr, notifOpts, browser = 'unknown') {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'granted') return false;
  
  try {
    // Firefox: simplificar opciones si hay problemas
    let options = notifOpts;
    if (browser === 'firefox') {
      // Firefox a veces tiene problemas con ciertas opciones
      options = {
        body: notifOpts.body,
        icon: notifOpts.icon,
        tag: notifOpts.tag,
        // Omitir requireInteraction, renotify si causan problemas
        ...(notifOpts.requireInteraction !== undefined && { requireInteraction: notifOpts.requireInteraction }),
        ...(notifOpts.renotify !== undefined && { renotify: notifOpts.renotify }),
        ...(notifOpts.silent !== undefined && { silent: notifOpts.silent }),
        ...(notifOpts.data && { data: notifOpts.data }),
      };
    }
    
    const n = new Notification(titleStr, options);
    
    // Event handlers
    n.onclick = () => {
      try {
        const raw = options.data && options.data.url;
        if (raw && typeof raw === 'string') {
          window.focus();
          window.location.assign(
            raw.startsWith('http')
              ? raw
              : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`,
          );
        } else {
          window.focus();
        }
      } catch (_) {
        try { window.focus(); } catch (_) {}
      }
      n.close();
    };
    
    n.onerror = (err) => {
      console.warn(`[webPush] Notification error en ${browser}:`, err);
    };
    
    n.onshow = () => {
      console.log(`[webPush] Notification mostrada en ${browser}`);
    };
    
    return true;
  } catch (err) {
    console.warn(`[webPush] new Notification() error en ${browser}:`, err);
    return false;
  }
}

/**
 * Solicita permiso de notificaciones de forma compatible con todos los navegadores.
 * Maneja tanto la API moderna (Promise) como la legacy (callback).
 * Incluye workaround específico para Firefox.
 */
async function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  
  const browser = detectBrowser();
  
  try {
    // Firefox: intentar Promise primero, luego callback si falla
    if (browser === 'firefox') {
      try {
        const result = await Notification.requestPermission();
        return result;
      } catch (promiseErr) {
        console.warn('[webPush] Firefox Promise falló, usando callback:', promiseErr);
        return new Promise((resolve) => {
          Notification.requestPermission((result) => {
            resolve(result);
          });
        });
      }
    }
    
    // Safari: solo callback (no soporta Promise en versiones antiguas)
    if (browser === 'safari') {
      return new Promise((resolve) => {
        Notification.requestPermission((result) => {
          resolve(result);
        });
      });
    }
    
    // Chrome, Edge: Promise moderna
    if (typeof Notification.requestPermission === 'function') {
      const result = await Notification.requestPermission();
      return result;
    }
    
    // Fallback para navegadores antiguos
    return new Promise((resolve) => {
      Notification.requestPermission((result) => {
        resolve(result);
      });
    });
  } catch (err) {
    console.warn('[webPush] Error solicitando permiso:', err);
    return 'denied';
  }
}

/**
 * Notificación del sistema cross-browser.
 * Estrategia adaptada por navegador:
 * - Chrome/Edge: Service Worker primero, new Notification() como fallback
 * - Firefox: new Notification() primero (más fiable), Service Worker como fallback
 * - Safari: solo new Notification() (SW limitado)
 * - Otros: new Notification() únicamente
 */
export async function notifyDesktopViaServiceWorkerOrFallback(title, body, options = {}) {
  const {
    tag,
    url = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search || ''}` : '/',
    requireInteraction = false,
    renotify = false,
    silent = false,
    icon,
    badge,
    inPageToastWhenVisible = true,
  } = options;

  const titleStr = title == null || title === '' ? 'Notificación' : String(title);
  const bodyStr = body == null ? '' : String(body);
  const browser = detectBrowser();

  if (typeof Notification === 'undefined') {
    console.warn('[webPush] API Notification no disponible');
    showInPageToastWhenTabVisible(titleStr, bodyStr, inPageToastWhenVisible, silent);
    return false;
  }

  // Solicitar permiso si no lo tenemos
  if (Notification.permission !== 'granted') {
    console.warn('[webPush] Permiso no concedido. Actual:', Notification.permission);
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('[webPush] Permiso denegado o no disponible:', permission);
      showInPageToastWhenTabVisible(titleStr, bodyStr, inPageToastWhenVisible, silent);
      return false;
    }
  }

  const notifOpts = buildShowOptions(body, { tag, url, requireInteraction, renotify, silent, icon, badge });

  let success = false;

  // Estrategia por navegador
  switch (browser) {
    case 'safari':
      // Safari: solo new Notification() (SW tiene soporte limitado)
      success = tryDirectNotification(titleStr, notifOpts, browser);
      break;
      
    case 'firefox':
      // Firefox: new Notification() primero (más fiable), SW como fallback
      success = tryDirectNotification(titleStr, notifOpts, browser);
      if (!success) {
        success = await trySwShowNotification(titleStr, notifOpts);
      }
      break;
      
    case 'chrome':
    case 'edge':
      // Chrome/Edge: SW primero (funciona mejor en background), new Notification() como fallback
      success = await trySwShowNotification(titleStr, notifOpts);
      if (!success) {
        success = tryDirectNotification(titleStr, notifOpts, browser);
      }
      break;
      
    default:
      // Navegadores desconocidos: intentar new Notification() únicamente
      success = tryDirectNotification(titleStr, notifOpts, browser);
      break;
  }

  // Toast en página solo cuando la pestaña está visible
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    showInPageToastWhenTabVisible(titleStr, bodyStr, inPageToastWhenVisible, silent);
  }

  if (!success) {
    console.warn(`[webPush] No se pudo mostrar notificación en ${browser}. title:`, titleStr);
  } else {
    console.log(`[webPush] Notificación enviada exitosamente en ${browser}`);
  }

  return success;
}

/**
 * Diagnóstico completo cross-browser para ejecutar desde la consola del navegador.
 * Uso: await window.__testNotificacion()
 */
export async function testDesktopNotification() {
  console.group('[webPush] Diagnóstico cross-browser de notificaciones');
  
  const browser = detectBrowser();
  console.log('Navegador detectado:', browser);
  console.log('Notification API disponible:', typeof Notification !== 'undefined');
  console.log('Permiso actual:', typeof Notification !== 'undefined' ? Notification.permission : 'N/A');
  console.log('ServiceWorker disponible:', 'serviceWorker' in navigator);
  console.log('visibilityState:', typeof document !== 'undefined' ? document.visibilityState : 'N/A');
  console.log('isSecureContext:', typeof window !== 'undefined' ? window.isSecureContext : 'N/A');
  console.log('hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');

  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    console.log('Solicitando permiso...');
    const perm = await requestNotificationPermission();
    console.log('Permiso obtenido:', perm);
  }

  if ('serviceWorker' in navigator && browser !== 'safari') {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      console.log('SW registrations:', regs.map(r => ({ scope: r.scope, active: !!r.active, waiting: !!r.waiting, installing: !!r.installing })));
      
      const ready = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) => setTimeout(() => rej(new Error('timeout 5s')), 5000)),
      ]);
      console.log('SW ready scope:', ready.scope, '| active state:', ready.active?.state);
    } catch (err) {
      console.warn('Error comprobando SW:', err);
    }
  }

  console.log(`\nEstrategia para ${browser}:`);
  switch (browser) {
    case 'safari':
      console.log('- Solo new Notification() (SW limitado en Safari)');
      break;
    case 'firefox':
      console.log('- new Notification() primero, SW como fallback');
      break;
    case 'chrome':
    case 'edge':
      console.log('- Service Worker primero, new Notification() como fallback');
      break;
    default:
      console.log('- Solo new Notification() (navegador no reconocido)');
      break;
  }

  const result = await notifyDesktopViaServiceWorkerOrFallback(
    '🔔 Prueba cross-browser',
    `Notificación de prueba en ${browser}. Si ves este aviso, las notificaciones funcionan correctamente.`,
    { tag: 'hs-test-' + Date.now(), renotify: true, url: window.location.pathname, inPageToastWhenVisible: true }
  );
  
  console.log('Resultado del test:', result ? '✅ enviado' : '❌ falló');
  
  if (!result) {
    console.log('\n🔧 Posibles soluciones por navegador:');
    console.log('Chrome/Edge: chrome://settings/content/notifications');
    console.log('Firefox: about:preferences#privacy → Notificaciones');
    console.log('Safari: Preferencias → Sitios web → Notificaciones');
    console.log('Windows: Configuración → Sistema → Notificaciones');
  }
  
  console.groupEnd();
  return result;
}